import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProject, getProjectBySubdomain } from '../../services/projects';
import { getSectionComponent } from '../../sections';
import Icon from '../../components/AppIcon';
import config from '../../config';
import { useTheme } from '../../hooks/useTheme';

const PublicPreview = ({ subdomainProjectId, subdomain }) => {
  const params = useParams();
  const projectId = subdomainProjectId || params.projectId;
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadProject() {
      try {
        let data;

        // Загрузка по subdomain (новый flow - без lookup в Cloud Function)
        if (subdomain) {
          data = await getProjectBySubdomain(subdomain);
          if (!data) {
            setError('Сайт не найден');
            return;
          }
        }
        // Загрузка по projectId (для кастомных доменов и прямых ссылок)
        else if (projectId) {
          data = await getProject(projectId);
          if (!data) {
            setError('Сайт не найден');
            return;
          }
          if (data.status !== 'published') {
            setError('Сайт не опубликован');
            return;
          }
        } else {
          setError('Сайт не указан');
          return;
        }

        setProject(data);
      } catch (err) {
        console.error('Error loading project:', err);
        setError('Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    }
    loadProject();
  }, [projectId, subdomain]);

  // Применяем тему (CSS переменные + Google Fonts)
  const themeKey = project?.settings?.theme || 'default';
  useTheme(themeKey);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} className="text-text-secondary" />
          </div>
          <h1 className="text-xl font-semibold mb-2">{error}</h1>
          <p className="text-text-secondary mb-6">
            Возможно, ссылка устарела или сайт был удалён
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          >
            <Icon name="LogIn" size={18} />
            Войти в {config.appName}
          </Link>
        </div>
      </div>
    );
  }

  const pages = project?.publishedPages || [];
  const mainPage = pages[0];
  const sections = mainPage?.sections || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Баннер приложения */}
      <div className="fixed bottom-4 right-4 z-50">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-full shadow-lg hover:shadow-xl transition-shadow text-sm"
        >
          <Icon name="Zap" size={16} className="text-primary" />
          <span>Создано в <strong>{config.appName}</strong></span>
        </a>
      </div>

      {/* Рендер секций */}
      {sections.length > 0 ? (
        sections.map((section, index) => {
          const SectionComponent = getSectionComponent(section.type);
          if (SectionComponent) {
            return (
              <div key={section.id || index} id={`section-${index}`}>
                <SectionComponent
                  data={section.content || {}}
                  styles={section.styles || {}}
                  theme={themeKey}
                />
              </div>
            );
          }
          // Fallback для неизвестных секций
          return null;
        })
      ) : (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center p-8">
            <Icon name="FileX" size={48} className="mx-auto mb-4 text-text-secondary opacity-50" />
            <p className="text-text-secondary">Страница пуста</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicPreview;
